import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Avatar } from '@mui/material';

/* ------------------------------------------------------------------ *
 * BWF · Members leaderboard — "Match Card" styling.
 * Expects the members array already sorted with `position` and
 * `trophy` ('gold' | 'silver' | 'bronze') assigned — group-details.js
 * already does this in its useEffect, so no logic changes needed.
 *
 * Usage (in group-details.js, members TabPanel):
 *   <MemberList members={group.members} currentUserId={authData?.user?.id} />
 * ------------------------------------------------------------------ */

const ORANGE = '#ff9800';
const LINE = '1px solid rgba(255,255,255,0.08)';
const LINE2 = '1px solid rgba(255,255,255,0.14)';
const MEDAL = { gold: '#e8b339', silver: '#c4cad3', bronze: '#cd7f32' };

const initials = (u) => (u?.username ? u.username.slice(0, 2).toUpperCase() : '??');
const avatarUrl = (u) => {
  const p = u?.profile?.image;
  if (!p) return null;
  return p.startsWith('http') ? p : `${process.env.REACT_APP_API_URL_BASE}${p}`;
};

function RankBadge({ position, trophy }) {
  const c = MEDAL[trophy];
  return (
    <Box sx={{
      width: 28, height: 28, borderRadius: '50%', flex: '0 0 auto',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 13, fontWeight: 800, fontFamily: '"Archivo", Inter, sans-serif',
      background: c || 'transparent', color: c ? '#1a1207' : '#b0b0b0',
      border: c ? 'none' : LINE2, boxShadow: c ? `0 0 0 2px ${c}33` : 'none',
    }}>{position}</Box>
  );
}

export default function MemberList({ members = [], currentUserId, groupId }) {
  const navigate = useNavigate();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {members.map((m) => {
        const you = m.user?.id === currentUserId;
        const top = m.position === 1;
        return (
          <Box key={m.id}
            onClick={() => navigate(`/member-history/${groupId}/${m.user.id}`, { state: { member: m } })}
            sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, p: '11px 14px', borderRadius: '12px', cursor: 'pointer',
            background: you ? 'rgba(255,255,255,0.05)' : (top ? 'rgba(232,179,57,0.07)' : '#1e1e1e'),
            border: you ? LINE2 : (top ? '1px solid rgba(232,179,57,0.3)' : LINE),
            transition: 'border-color .15s ease',
            '&:hover': { borderColor: 'rgba(255,152,0,0.5)' },
          }}>
            <RankBadge position={m.position} trophy={m.trophy} />
            <Avatar src={avatarUrl(m.user)} sx={{
              width: 38, height: 38, fontSize: 14, fontWeight: 700, bgcolor: 'rgba(255,255,255,0.12)',
              boxShadow: you ? `0 0 0 2px ${ORANGE}` : 'none',
            }}>{initials(m.user)}</Avatar>
            <Typography sx={{ flex: 1, fontSize: 15, fontWeight: you ? 700 : 600, color: '#fff', display: 'flex', alignItems: 'center' }}>
              {m.user?.username}
              {you && (
                <Box component="span" sx={{ ml: 1, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, color: ORANGE, border: LINE2, borderRadius: 999, px: 0.875, py: '1px' }}>YOU</Box>
              )}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography sx={{ fontFamily: '"Archivo", Inter, sans-serif', fontWeight: 800, fontSize: 18, color: '#fff' }}>{m.points}</Typography>
              <Typography sx={{ fontSize: 11, color: '#b0b0b0', fontWeight: 600 }}>pts</Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
